/**
 * PairingRuleModal — add/edit a 商品搭配規則 (Feat #2168, G-2166-K-04).
 *
 * Create: component product search-select (excludes the parent itself AND
 * every already-paired component — the (parent, component) pair is the rule's
 * identity across active + inactive, so the mock's 400 duplicate branch is
 * unreachable from this form), Mandatory/Optional toggle, 數量比 pair inputs
 * (parentQty defaults 1, Figma 1:16), notes.
 *
 * O1 OPEN (CORRECTED): the 售價原則 field is DISABLED with an OPEN badge —
 * there is NO six-mode choice UI and NO pre-selected mode. Submit never
 * carries pricingMode (create persists null; edit leaves the stored value
 * untouched) — no rule may behave as if a pricing mode was chosen, pending
 * the 林芊/Judy ruling.
 *
 * Edit: parent/component are IMMUTABLE on the PATCH contract — the component
 * renders as static identity text (a different pair is a NEW rule), the
 * editable fields round-trip through `buildUpdatePairingRuleRequest`.
 *
 * House modal conventions: zodResolver (no Controller rules), width 800,
 * 44px ok/cancel buttons, formStyles, Add/Save okText by mode. The schema's
 * error messages are i18n KEYS resolved at display time. Schema lives HERE
 * (not schemas.ts — that file belongs to another slice; recorded followUp).
 */

import { useEffect, useMemo } from 'react';
import { Col, Input, InputNumber, Modal, Radio, Row, Select, Tag, Typography, theme } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import {
  PAIRING_PRICING_MODE_LABEL_KEYS,
  PAIRING_TYPE_LABEL_KEYS,
  usePairingRuleMutations,
} from '@/hooks/usePairingRules';
import { useProductOptions } from '@/hooks/useProducts';
import type { Product } from '@/types/api/product';
import type {
  CreateProductPairingRuleRequest,
  PairingType,
  ProductPairingRule,
  UpdateProductPairingRuleRequest,
} from '@/types/api/pairingRule';

const { Text } = Typography;

/* eslint-disable react-refresh/only-export-components -- schema + pure
   round-trip builders are exported alongside the modal so the vitest proves
   the EXACT form → request path (house schemas/section precedent). */

// z.enum needs literal tuples; `satisfies` pins them to the API unions so a
// contract change breaks the build here, not silently at runtime.
// NOTE: deliberately NO pricing-mode tuple — O1 is OPEN, the form carries no
// pricingMode at all (no choice UI, no default, nothing submitted).
const PAIRING_TYPE_ENUM = ['mandatory', 'optional'] as const satisfies readonly PairingType[];

/** Figma 1:16 — both sides of the 數量比 are positive integers. */
const ratioQty = z
  .number({
    required_error: 'products:pairing.validation.ratioPositiveInt',
    invalid_type_error: 'products:pairing.validation.ratioPositiveInt',
  })
  .int('products:pairing.validation.ratioPositiveInt')
  .min(1, 'products:pairing.validation.ratioPositiveInt');

export const pairingRuleFormSchema = z.object({
  componentProductId: z.string().min(1, 'products:pairing.validation.componentRequired'),
  pairingType: z.enum(PAIRING_TYPE_ENUM),
  parentQty: ratioQty,
  componentQty: ratioQty,
  notes: z.string().trim().optional(),
});

export type PairingRuleFormData = z.infer<typeof pairingRuleFormSchema>;

/** Hydrate the form — create defaults (parentQty 1; NO pricing mode — O1 open) or the rule under edit. */
export function pairingRuleFormValues(rule: ProductPairingRule | null): PairingRuleFormData {
  if (!rule) {
    return {
      componentProductId: '',
      pairingType: 'mandatory',
      parentQty: 1,
      componentQty: 1,
      notes: '',
    };
  }
  return {
    componentProductId: rule.componentProductId,
    pairingType: rule.pairingType,
    parentQty: rule.qtyRatio.parentQty,
    componentQty: rule.qtyRatio.componentQty,
    notes: rule.notes ?? '',
  };
}

/**
 * Form → POST body (parent rides the path; blank notes normalise to null).
 * pricingMode is deliberately ABSENT — O1 open, the mock persists null.
 */
export function buildCreatePairingRuleRequest(
  form: PairingRuleFormData
): CreateProductPairingRuleRequest {
  return {
    componentProductId: form.componentProductId,
    pairingType: form.pairingType,
    qtyRatio: { parentQty: form.parentQty, componentQty: form.componentQty },
    notes: form.notes?.trim() ? form.notes.trim() : null,
  };
}

/**
 * Form → PATCH body — editable fields only (parent/component immutable).
 * pricingMode is deliberately ABSENT — the stored value is never touched
 * from this form while O1 stays open.
 */
export function buildUpdatePairingRuleRequest(
  form: PairingRuleFormData
): UpdateProductPairingRuleRequest {
  return {
    pairingType: form.pairingType,
    qtyRatio: { parentQty: form.parentQty, componentQty: form.componentQty },
    notes: form.notes?.trim() ? form.notes.trim() : null,
  };
}

/* eslint-enable react-refresh/only-export-components */

interface PairingRuleModalProps {
  open: boolean;
  product: Product;
  /** null = create; a rule = edit (component immutable). */
  rule: ProductPairingRule | null;
  /** All existing rules of the parent — excludes already-paired components. */
  existingRules: readonly ProductPairingRule[];
  onCancel: () => void;
  onSaved: () => void;
}

export const PairingRuleModal = ({
  open,
  product,
  rule,
  existingRules,
  onCancel,
  onSaved,
}: PairingRuleModalProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { handleError, handleSuccess } = useErrorHandler();
  const { createRule, updateRule } = usePairingRuleMutations(product.id);
  const isEditMode = rule != null;

  const { products: allProducts, isLoading: productsLoading } = useProductOptions('all');

  // Parent ≠ component + the (parent, component) pair is the identity across
  // active AND inactive rules — exclude both up front (AddVendorLineModal
  // linkedVendorIds precedent).
  const excludedIds = useMemo(
    () => new Set([product.id, ...existingRules.map((item) => item.componentProductId)]),
    [product.id, existingRules]
  );

  const componentOptions = useMemo(
    () =>
      allProducts
        .filter((item) => !excludedIds.has(item.id))
        .map((item) => ({
          value: item.id,
          label: item.sku ? `${item.displayName} (${item.sku})` : item.displayName,
        })),
    [allProducts, excludedIds]
  );

  const editComponent = useMemo(
    () => (rule ? allProducts.find((item) => item.id === rule.componentProductId) : undefined),
    [allProducts, rule]
  );

  const defaultFormValues = useMemo<PairingRuleFormData>(() => pairingRuleFormValues(rule), [rule]);

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<PairingRuleFormData>({
    resolver: zodResolver(pairingRuleFormSchema),
    defaultValues: defaultFormValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open) reset(defaultFormValues);
  }, [open, defaultFormValues, reset]);

  const onSubmit = async (data: PairingRuleFormData) => {
    try {
      if (isEditMode && rule) {
        await updateRule(rule.id, buildUpdatePairingRuleRequest(data));
        handleSuccess(t('products:pairing.messages.updated'));
      } else {
        await createRule(buildCreatePairingRuleRequest(data));
        handleSuccess(t('products:pairing.messages.created'));
      }
      onSaved();
      onCancel();
    } catch (submitError) {
      handleError(submitError);
    }
  };

  const requiredMark = <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>;

  // O1 OPEN — the field is read-only pending the ruling. A stored explicit
  // mode (edit) still displays its label; otherwise the bilingual OPEN copy.
  const storedPricingMode = rule?.pricingMode ?? null;
  const pricingModeDisplay = storedPricingMode
    ? t(PAIRING_PRICING_MODE_LABEL_KEYS[storedPricingMode])
    : t('products:pairing.openField');

  return (
    <Modal
      title={isEditMode ? t('products:pairing.editRule') : t('products:pairing.addRule')}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit(onSubmit)}
      okText={isEditMode ? t('common:actions.save') : t('common:actions.add')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={isSubmitting}
      width={800}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
    >
      <form>
        <div style={formStyles.fieldContainerStyle}>
          <Text strong style={formStyles.sectionTitleStyle}>
            {t('products:pairing.sectionTitle')}
          </Text>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Text style={formStyles.labelStyle}>
                {!isEditMode && requiredMark}
                {t('products:pairing.fields.component')}
              </Text>
              {isEditMode ? (
                // Identity is immutable on PATCH — static display, not a
                // disabled control (a different pair is a NEW rule).
                <div
                  style={{
                    height: `${controlHeightTokens.controlHeightXL}px`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Text strong>
                    {editComponent?.displayName ??
                      t('products:pairing.componentFallback', {
                        id: rule?.componentProductId,
                      })}
                  </Text>
                  <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                    {editComponent?.sku ?? rule?.componentProductId}
                  </Text>
                </div>
              ) : (
                <Controller
                  name="componentProductId"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Select
                        value={field.value || undefined}
                        showSearch
                        loading={productsLoading}
                        optionFilterProp="label"
                        size="large"
                        options={componentOptions}
                        placeholder={t('products:pairing.fields.componentHint')}
                        status={
                          errors.componentProductId && touchedFields.componentProductId
                            ? 'error'
                            : undefined
                        }
                        style={formStyles.inputStyle}
                        onChange={(value) => {
                          field.onChange(value ?? '');
                          trigger('componentProductId');
                        }}
                      />
                      {errors.componentProductId && touchedFields.componentProductId && (
                        <Text style={formStyles.errorStyle}>
                          {t(errors.componentProductId.message ?? '')}
                        </Text>
                      )}
                    </>
                  )}
                />
              )}
            </Col>
            <Col span={8}>
              <Text style={formStyles.labelStyle}>{t('products:pairing.fields.type')}</Text>
              <Controller
                name="pairingType"
                control={control}
                render={({ field }) => (
                  <Radio.Group
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    style={{
                      display: 'flex',
                      height: `${controlHeightTokens.controlHeightXL}px`,
                      alignItems: 'center',
                    }}
                  >
                    <Radio value="mandatory">{t(PAIRING_TYPE_LABEL_KEYS.mandatory)}</Radio>
                    <Radio value="optional">{t(PAIRING_TYPE_LABEL_KEYS.optional)}</Radio>
                  </Radio.Group>
                )}
              />
            </Col>
            <Col span={8}>
              <Text style={formStyles.labelStyle}>
                {requiredMark}
                {t('products:pairing.fields.parentQty')}
              </Text>
              <Controller
                name="parentQty"
                control={control}
                render={({ field }) => (
                  <>
                    <InputNumber
                      value={field.value ?? null}
                      onChange={(value) => {
                        field.onChange(value);
                        trigger('parentQty');
                      }}
                      onBlur={field.onBlur}
                      size="large"
                      min={1}
                      precision={0}
                      status={errors.parentQty && touchedFields.parentQty ? 'error' : undefined}
                      style={formStyles.inputStyle}
                    />
                    {errors.parentQty && touchedFields.parentQty && (
                      <Text style={formStyles.errorStyle}>
                        {t(errors.parentQty.message ?? '')}
                      </Text>
                    )}
                  </>
                )}
              />
            </Col>
            <Col span={8}>
              <Text style={formStyles.labelStyle}>
                {requiredMark}
                {t('products:pairing.fields.componentQty')}
              </Text>
              <Controller
                name="componentQty"
                control={control}
                render={({ field }) => (
                  <>
                    <InputNumber
                      value={field.value ?? null}
                      onChange={(value) => {
                        field.onChange(value);
                        trigger('componentQty');
                      }}
                      onBlur={field.onBlur}
                      size="large"
                      min={1}
                      precision={0}
                      status={
                        errors.componentQty && touchedFields.componentQty ? 'error' : undefined
                      }
                      style={formStyles.inputStyle}
                    />
                    {errors.componentQty && touchedFields.componentQty && (
                      <Text style={formStyles.errorStyle}>
                        {t(errors.componentQty.message ?? '')}
                      </Text>
                    )}
                  </>
                )}
              />
            </Col>
            <Col span={24}>
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {t('products:pairing.fields.qtyHint')}
              </Text>
            </Col>
            <Col span={24}>
              <span className="tw-inline-flex tw-items-center tw-gap-2">
                <Text style={formStyles.labelStyle}>
                  {t('products:pairing.fields.pricingMode')}
                </Text>
                <Tag
                  style={{
                    margin: 0,
                    color: token.colorTextSecondary,
                    backgroundColor: token.colorFillQuaternary,
                    borderColor: token.colorBorderSecondary,
                  }}
                >
                  {t('products:pairing.openBadge')}
                </Tag>
              </span>
              {/* O1 OPEN — DISABLED, no six-mode choice UI, nothing submitted. */}
              <Input
                disabled
                size="large"
                value={pricingModeDisplay}
                style={formStyles.inputStyle}
              />
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {t('products:pairing.openBadgeHint')}
              </Text>
            </Col>
            <Col span={24}>
              <Text style={formStyles.labelStyle}>{t('products:pairing.fields.notes')}</Text>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <Input.TextArea
                    {...field}
                    size="large"
                    style={{ ...formStyles.inputStyle, height: '96px' }}
                  />
                )}
              />
            </Col>
          </Row>
        </div>
      </form>
    </Modal>
  );
};

export default PairingRuleModal;
